import {
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material'
import { useState } from 'react'
import { isEmpty, isNotEmpty } from 'utils/data_manipulator'
import { TruncateText } from 'utils/StrUtils'

type ILoadsheetTable = {
  columns: any[]
  data: any[]
}

export function LoadsheetTable({ columns, data }: ILoadsheetTable) {
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: 'white',
      color: theme.palette.common.black,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
    },
  }))

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value)
    setPage(0)
  }

  return (
    <>
      <TableContainer
        sx={{ maxHeight: 900, overflowX: 'scroll' }}
        className="custom-scroll scrollbar-thick"
      >
        <Table stickyHeader size="small" className="loadsheet-data" aria-label="sticky table">
          <TableHead>
            <TableRow>
              <StyledTableCell key="row_status" align="left" width={16}>
                Row Status
              </StyledTableCell>
              {columns?.map((value, index) => {
                return (
                  <StyledTableCell key={index} align="left">
                    {value.header}
                  </StyledTableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            <LoadsheetTBodyContent
              fileValidation={data}
              key={'loadsheet-validation-body'}
              columns={columns}
              page={page}
              rowsPerPage={rowsPerPage}
            />
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={data?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        className="loadsheet-validation-pagination"
      />
    </>
  )
}

type PropsType = {
  page: number
  rowsPerPage: number
  fileValidation: any[]
  columns: readonly any[]
}

export default function LoadsheetTBodyContent(props: PropsType) {
  const { page, rowsPerPage, fileValidation, columns } = props

  const BodyCellStyle: any = {
    wordBreak: 'break-word',
    maxWidth: 500,
  }

  const processRowData = (row: any) => {
    return row.data?.reduce(
      (acc: any, curr: any) => (
        (acc[curr.id] = {
          value: curr.value,
          valid: curr.valid,
          error_message: curr.error_message,
        }),
        acc
      ),
      {}
    )
  }
  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }))

  return (
    <>
      {fileValidation &&
        fileValidation
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((row, index) => {
            const rowData = processRowData(row)

            return (
              <StyledTableRow hover role="checkbox" tabIndex={-1} key={`${index + page}`}>
                <>
                  <TableCell
                    className={row.valid ? 'valid' : 'in-valid'}
                    align="center"
                    key={`${index + page}_row_status`}
                  >
                    {row.valid && <span className="text-white font-weight-bold">Valid</span>}
                    {!row.valid && <span className="text-white font-weight-bold">Invalid</span>}
                  </TableCell>
                  {row.row_number && (
                    <TableCell key={`${index + page}_row_number`} align="center">
                      {row.row_number}
                    </TableCell>
                  )}
                  {columns?.map((column) => {
                    const value = TruncateText(rowData[column?.accessorKey]?.value, 100)

                    return (
                      <TableCell align={column.align} key={`${index + page + column.id}`}>
                        {isNotEmpty(rowData[column.accessorKey]) && (
                          <div
                            className="d-flex"
                            style={{
                              ...BodyCellStyle,
                              minWidth: column.minSize ?? 100,
                              width: column.size,
                              flexDirection: 'column',
                            }}
                          >
                            {isNotEmpty(value) && column.format ? column.format(value) : value}
                            {!rowData[column.accessorKey].valid && (
                              <Tooltip
                                title={rowData[column.accessorKey].error_message || 'Invalid data'}
                              >
                                <span
                                  className="badge badge-met badge-danger mr-2"
                                  style={{ width: 45, cursor: 'pointer' }}
                                >
                                  Invalid
                                </span>
                              </Tooltip>
                            )}
                          </div>
                        )}
                      </TableCell>
                    )
                  })}
                </>
              </StyledTableRow>
            )
          })}
    </>
  )
}
